import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Image, Send, Camera, X, Heart, Sparkles } from "lucide-react";
import { uploadPhoto } from "../utils/photoApi";
import Button from "./Button";

const PhotoUpload = ({ onPhotoUploaded = () => {} }) => {
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaderName, setUploaderName] = useState(
    localStorage.getItem("uploaderName") || "",
  );
  const [uploaderEmail, setUploaderEmail] = useState(
    localStorage.getItem("uploaderEmail") || "",
  );
  const [compressing, setCompressing] = useState(false);
  const [compressionStats, setCompressionStats] = useState(null);
  const [showSocialPopup, setShowSocialPopup] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Comprimir imagen antes de subir con optimizaciones avanzadas
  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      // Validar que el archivo sea una imagen
      if (!file.type.startsWith("image/")) {
        reject(new Error("El archivo no es una imagen válida"));
        return;
      }

      const reader = new FileReader();

      reader.onerror = () => reject(new Error("Error al leer el archivo"));

      reader.onload = (e) => {
        const img = new window.Image();

        img.onerror = () => reject(new Error("Error al cargar la imagen"));

        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            let width = img.width;
            let height = img.height;

            // Calcular tamaño máximo manteniendo aspect ratio
            const aspectRatio = width / height;

            if (width > maxWidth || height > maxWidth) {
              if (aspectRatio > 1) {
                // Landscape
                width = maxWidth;
                height = maxWidth / aspectRatio;
              } else {
                // Portrait
                height = maxWidth;
                width = maxWidth * aspectRatio;
              }
            }

            // Redondear a píxeles enteros
            width = Math.round(width);
            height = Math.round(height);

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d", { alpha: false });

            // Mejorar calidad de renderizado
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            // Fondo blanco para transparencias
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, width, height);

            // Dibujar imagen optimizada
            ctx.drawImage(img, 0, 0, width, height);

            // Calcular calidad basada en tamaño del archivo
            const fileSizeKB = file.size / 1024;
            let dynamicQuality = quality;

            if (fileSizeKB > 5000) {
              dynamicQuality = 0.75; // Imágenes muy grandes
            } else if (fileSizeKB > 2000) {
              dynamicQuality = 0.8; // Imágenes grandes
            }

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error("Error al comprimir la imagen"));
                  return;
                }

                const originalSize = file.size;
                const compressedSize = blob.size;
                const reduction = (
                  ((originalSize - compressedSize) / originalSize) *
                  100
                ).toFixed(1);

                const stats = {
                  originalSize: (originalSize / 1024 / 1024).toFixed(2),
                  compressedSize: (compressedSize / 1024 / 1024).toFixed(2),
                  reduction: reduction,
                  dimensions: `${width}×${height}px`,
                };

                console.log(
                  `📸 Imagen comprimida: ${stats.originalSize}MB → ${stats.compressedSize}MB (${reduction}% reducción)`,
                );

                const compressedFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, ".jpg"),
                  {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  },
                );

                // Agregar stats al archivo para mostrarlos en la UI
                compressedFile.compressionStats = stats;

                resolve(compressedFile);
              },
              "image/jpeg",
              dynamicQuality,
            );
          } catch (error) {
            reject(new Error("Error durante la compresión"));
          }
        };

        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/"),
    );

    if (selectedFiles.length === 0) return;

    setCompressing(true);
    setCompressionStats(null);

    try {
      // Comprimir imágenes una por una para mostrar progreso
      const compressedFiles = [];
      let totalOriginal = 0;
      let totalCompressed = 0;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const compressed = await compressImage(file);
        compressedFiles.push(compressed);

        if (compressed.compressionStats) {
          totalOriginal += parseFloat(compressed.compressionStats.originalSize);
          totalCompressed += parseFloat(
            compressed.compressionStats.compressedSize,
          );
        }
      }

      // Mostrar estadísticas totales
      if (compressedFiles.length > 0) {
        const avgReduction = (
          ((totalOriginal - totalCompressed) / totalOriginal) *
          100
        ).toFixed(1);
        setCompressionStats({
          count: compressedFiles.length,
          originalSize: totalOriginal.toFixed(2),
          compressedSize: totalCompressed.toFixed(2),
          reduction: avgReduction,
        });

        // Ocultar mensaje después de 3 segundos
        setTimeout(() => setCompressionStats(null), 3000);
      }

      setFiles((prev) => [...prev, ...compressedFiles]);
    } catch (error) {
      console.error("Error al comprimir:", error);
      setMessage("Error al comprimir las imágenes");
    } finally {
      setCompressing(false);
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setCompressing(true);
      setCompressionStats(null);

      try {
        const compressed = await compressImage(file);

        if (compressed.compressionStats) {
          setCompressionStats({
            count: 1,
            ...compressed.compressionStats,
          });
          setTimeout(() => setCompressionStats(null), 3000);
        }

        setFiles((prev) => [...prev, compressed]);
      } catch (error) {
        console.error("Error al comprimir:", error);
        setMessage("Error al comprimir la imagen");
      } finally {
        setCompressing(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let completedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("image", file);
      formData.append("uploader", uploaderName.trim() || "Usuario Anónimo");
      formData.append("email", uploaderEmail.trim());
      formData.append("message", message);

      try {
        // Callback de progreso para esta foto individual
        const onProgress = (percentCompleted) => {
          // Calcular progreso total: archivos completados + progreso actual
          const baseProgress = (completedFiles / totalFiles) * 100;
          const currentFileProgress = percentCompleted / totalFiles;
          const totalProgress = Math.round(baseProgress + currentFileProgress);
          setUploadProgress(totalProgress);
        };

        const response = await uploadPhoto(formData, onProgress);
        console.log("Respuesta del servidor:", response);

        if (response && response.url) {
          completedFiles++;
          setUploadProgress(Math.round((completedFiles / totalFiles) * 100));

          onPhotoUploaded({
            id: response.id,
            url: response.url,
            image_sizes: response.sizes ? JSON.stringify(response.sizes) : null,
            uploader: uploaderName.trim() || "Usuario Anónimo",
            message,
            likes: 0,
            comments: [],
            commentsLoaded: true,
            created_at: new Date().toISOString(),
          });
        } else {
          console.error("Respuesta inválida del servidor:", response);
        }
      } catch (error) {
        console.error("Error subiendo foto:", error);
        console.error(
          "Detalles del error:",
          error.response?.data || error.message,
        );
      }
    }

    setFiles([]);
    setMessage("");
    setUploading(false);
    setUploadProgress(0);

    // Mostrar popup de redes sociales solo la primera vez
    const hasSeenSocialPopup = localStorage.getItem("hasSeenSocialPopup");
    if (!hasSeenSocialPopup && completedFiles > 0) {
      setTimeout(() => {
        // Scroll al top para asegurar que el modal sea visible
        window.scrollTo({ top: 0, behavior: "smooth" });
        setShowSocialPopup(true);
        localStorage.setItem("hasSeenSocialPopup", "true");
      }, 1000); // Esperar 1 segundo después de subir para mejor UX
    }
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <>
      <motion.div
        className="bg-gradient-to-br from-white via-neutral-ivory to-accent-rose/20 backdrop-blur-xl border-2 border-accent-gold/30 rounded-card p-5 sm:p-7 mb-6 sm:mb-8 shadow-elegant mx-2 sm:mx-0"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Header con nombre del usuario - estilo Puebla */}
          <div className="mb-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-primary to-primary-light rounded-elegant shadow-soft">
                <Upload className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-semibold text-primary">
                  Comparte tus momentos
                </h2>
                <p className="font-sans text-sm text-neutral-warm mt-0.5">
                  Hola,{" "}
                  <span className="font-semibold text-secondary">
                    {uploaderName || "Invitado"}
                  </span>{" "}
                  👋
                </p>
              </div>
            </div>

            {/* Campos de nombre y email */}
            <div className="space-y-3">
              <input
                type="text"
                value={uploaderName}
                onChange={(e) => {
                  setUploaderName(e.target.value);
                  localStorage.setItem("uploaderName", e.target.value);
                }}
                placeholder="Tu nombre"
                className="w-full p-3 font-sans border-2 border-accent-gold/30 rounded-elegant focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary text-base transition-all bg-white/80 backdrop-blur-sm text-neutral-warm placeholder:text-neutral-warm/60"
              />
              <input
                type="email"
                value={uploaderEmail}
                onChange={(e) => {
                  setUploaderEmail(e.target.value);
                  localStorage.setItem("uploaderEmail", e.target.value);
                }}
                placeholder="Tu correo electrónico"
                className="w-full p-3 font-sans border-2 border-accent-gold/30 rounded-elegant focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary text-base transition-all bg-white/80 backdrop-blur-sm text-neutral-warm placeholder:text-neutral-warm/60"
              />
            </div>
          </div>

          {/* Botones de captura con diseño elegante */}
          <div className="grid grid-cols-2 gap-4">
            <motion.button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-dashed border-primary/40 rounded-elegant bg-gradient-to-br from-accent-rose to-white hover:from-accent-rose/80 hover:to-neutral-ivory transition-all active:scale-95 shadow-soft hover:shadow-elegant"
              whileTap={{ scale: 0.95 }}
            >
              <div className="p-3 bg-gradient-to-br from-primary to-primary-light rounded-full shadow-soft">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <span className="font-sans text-sm font-semibold text-primary">
                Tomar foto
              </span>
            </motion.button>

            <motion.button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-3 p-6 border-2 border-dashed border-secondary/40 rounded-elegant bg-gradient-to-br from-accent-rose to-white hover:from-accent-rose/80 hover:to-neutral-ivory transition-all active:scale-95 shadow-soft hover:shadow-elegant"
              whileTap={{ scale: 0.95 }}
            >
              <div className="p-3 bg-gradient-to-br from-secondary to-secondary-light rounded-full shadow-soft">
                <Image className="w-6 h-6 text-white" />
              </div>
              <span className="font-sans text-sm font-semibold text-secondary">
                Galería
              </span>
            </motion.button>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraCapture}
              className="hidden"
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Indicador de compresión */}
          <AnimatePresence>
            {compressing && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-gradient-to-r from-primary/10 to-secondary/10 border-2 border-primary/30 rounded-elegant p-4 backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
                  <span className="font-sans text-sm font-medium text-primary">
                    Comprimiendo imágenes...
                  </span>
                </div>
              </motion.div>
            )}

            {compressionStats && !compressing && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400/50 rounded-elegant p-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">✅</span>
                    <span className="font-sans text-sm font-semibold text-green-700">
                      {compressionStats.count === 1
                        ? "Imagen comprimida exitosamente"
                        : `${compressionStats.count} imágenes comprimidas`}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs font-sans">
                    <div className="bg-white/60 rounded-lg p-2">
                      <div className="text-gray-500">Original</div>
                      <div className="font-bold text-gray-700">
                        {compressionStats.originalSize} MB
                      </div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-2">
                      <div className="text-gray-500">Comprimido</div>
                      <div className="font-bold text-green-600">
                        {compressionStats.compressedSize} MB
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      📉 {compressionStats.reduction}% reducido
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Preview de fotos con estilo elegante */}
          <AnimatePresence>
            {files.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-accent-gold/20">
                  <span className="font-sans text-sm font-medium text-primary">
                    {files.length} foto{files.length > 1 ? "s" : ""}{" "}
                    seleccionada
                    {files.length > 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFiles([])}
                    className="font-sans text-sm text-secondary hover:text-secondary-dark font-medium transition-colors"
                  >
                    Limpiar todo
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {files.map((file, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      className="relative aspect-square group"
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-elegant shadow-soft border-2 border-accent-gold/20"
                      />

                      {/* Badge de tamaño comprimido */}
                      {file.compressionStats && (
                        <div className="absolute bottom-1 left-1 bg-green-500/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                          {file.compressionStats.compressedSize}MB
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="absolute -top-2 -right-2 bg-gradient-to-br from-secondary to-secondary-dark text-white rounded-full w-8 h-8 flex items-center justify-center shadow-elegant hover:scale-110 transition-all active:scale-95"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mensaje opcional con diseño Puebla */}
          {files.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
            >
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Agrega un comentario especial... 💭"
                className="w-full p-4 font-sans border-2 border-accent-gold/30 rounded-elegant focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary resize-none text-base transition-all bg-white/80 backdrop-blur-sm text-neutral-warm placeholder:text-neutral-warm/70"
                rows="3"
              />
            </motion.div>
          )}

          {/* Botón de envío con diseño elegante */}
          <AnimatePresence>
            {files.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={uploading}
                  icon={uploading ? null : <Send className="w-6 h-6" />}
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-3 w-full">
                      {/* Spinner animado con pulso */}
                      <motion.div
                        className="relative w-6 h-6"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <div className="absolute inset-0 border-3 border-white/30 rounded-full" />
                        <div className="absolute inset-0 border-3 border-white border-t-transparent rounded-full" />
                      </motion.div>

                      {/* Contador con transición suave */}
                      <motion.span
                        className="text-lg font-bold tabular-nums"
                        key={uploadProgress}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        Subiendo {uploadProgress}%
                      </motion.span>
                    </div>
                  ) : (
                    <span className="text-lg">Compartir ahora</span>
                  )}
                </Button>

                {/* Barra de progreso fenomenal con múltiples efectos */}
                {uploading && (
                  <motion.div
                    className="mt-4 space-y-2"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {/* Barra de progreso con gradiente animado */}
                    <div className="relative bg-gradient-to-r from-accent-rose/30 via-neutral-ivory/50 to-accent-rose/30 rounded-full h-3 overflow-hidden shadow-inner">
                      {/* Brillo de fondo animado */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        animate={{ x: ["-100%", "200%"] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />

                      {/* Barra de progreso principal */}
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        className="relative h-full bg-gradient-to-r from-primary via-secondary to-accent-gold rounded-full shadow-elegant overflow-hidden"
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      >
                        {/* Efecto de brillo que se mueve */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                          animate={{ x: ["-100%", "200%"] }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        />

                        {/* Partículas en la punta */}
                        <motion.div
                          className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-lg"
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [1, 0.5, 1],
                          }}
                          transition={{ duration: 0.8, repeat: Infinity }}
                        />
                      </motion.div>
                    </div>

                    {/* Información adicional */}
                    <div className="flex items-center justify-between px-1">
                      <motion.p
                        className="text-xs font-sans text-neutral-warm"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {uploadProgress < 100
                          ? "✨ Optimizando imágenes..."
                          : "✅ ¡Completado!"}
                      </motion.p>
                      <motion.p
                        className="text-xs font-sans font-semibold text-primary"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                      >
                        {files.length} foto{files.length > 1 ? "s" : ""}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>

      {/* Popup de invitación a redes sociales - Fuera del contenedor principal */}
      <AnimatePresence>
        {showSocialPopup && (
          <>
            {/* Backdrop con blur mejorado */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setShowSocialPopup(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-md"
              style={{ zIndex: 2147483646 }}
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
                duration: 0.4,
              }}
              className="fixed inset-0 flex items-center justify-center p-3 pointer-events-none overflow-y-auto"
              style={{ zIndex: 2147483647 }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl max-w-sm w-full relative overflow-hidden pointer-events-auto my-auto"
              >
                {/* Header con gradiente decorativo */}
                <div className="relative bg-gradient-to-br from-primary via-secondary to-accent-rose pt-6 pb-8 px-5 overflow-hidden">
                  {/* Patrón de fondo decorativo */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-20 translate-x-20"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-16 -translate-x-16"></div>
                  </div>

                  {/* Botón cerrar */}
                  <button
                    onClick={() => setShowSocialPopup(false)}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>

                  {/* Icono principal con animación de éxito */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: 0.2,
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                    }}
                    className="relative flex justify-center mb-3"
                  >
                    <div className="relative">
                      {/* Anillo pulsante */}
                      <motion.div
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="absolute inset-0 bg-accent-gold rounded-full blur-xl"
                      />

                      {/* Círculo del icono */}
                      <div className="relative bg-white/95 backdrop-blur-sm rounded-full p-4 shadow-xl">
                        <motion.div
                          animate={{
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <Camera className="w-8 h-8 text-primary" />
                        </motion.div>

                        {/* Checkmark animado */}
                        <motion.div
                          initial={{ scale: 0, rotate: -45 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ delay: 0.4, type: "spring" }}
                          className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 shadow-lg"
                        >
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Título con emoji */}
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="font-serif text-xl font-bold text-white text-center leading-tight"
                  >
                    ¡Foto Compartida! 🎉
                  </motion.h3>

                  {/* Subtítulo */}
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="font-sans text-white/90 text-center text-sm mt-2"
                  >
                    Tu momento especial ya está en la galería
                  </motion.p>
                </div>

                {/* Contenido del modal */}
                <div className="px-5 py-4">
                  {/* Mensaje persuasivo */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mb-4"
                  >
                    <p className="font-sans text-gray-700 text-center text-sm leading-relaxed">
                      <span className="font-bold text-primary block mb-1">
                        ¡No olvides seguirnos!
                      </span>
                      Descubre más momentos especiales y promociones exclusivas
                      en Instagram
                    </p>
                  </motion.div>

                  {/* Card de Instagram con animación */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                    className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 mb-4 border-2 border-purple-100"
                  >
                    <div className="flex items-center justify-center gap-3 mb-2">
                      {/* Logo Instagram estilizado */}
                      <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl p-2">
                        <Camera className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-sans text-xs text-gray-600 leading-tight">
                          Síguenos en Instagram
                        </p>
                        <p className="font-sans text-base font-bold text-gray-900 leading-tight">
                          @a_la_vueltacabina_360
                        </p>
                      </div>
                    </div>

                    {/* Beneficios compactos */}
                    <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-purple-200/50">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                        <span>Eventos</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Heart
                          className="w-3.5 h-3.5 text-pink-500"
                          fill="currentColor"
                        />
                        <span>Promociones</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Camera className="w-3.5 h-3.5 text-orange-500" />
                        <span>Galería</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Botones de acción mejorados */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="space-y-2"
                  >
                    {/* Botón principal - Instagram */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowSocialPopup(false);
                        window.open(
                          "https://www.instagram.com/a_la_vueltacabina_360/",
                          "_blank",
                        );
                      }}
                      className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white font-bold py-3 px-5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
                    >
                      <Camera className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                      <span className="text-base">Seguir en Instagram</span>
                      <motion.div
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.div>
                    </motion.button>

                    {/* Botón secundario */}
                    <button
                      onClick={() => setShowSocialPopup(false)}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-5 rounded-xl transition-all duration-300 text-sm"
                    >
                      Continuar sin seguir
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default PhotoUpload;
